/**
 * device/apps/htooth.ts — hTooth Device Controller
 *
 * A Bluetooth-style app for controlling audio-emitting furniture in the room.
 * Scans for nearby furniture that has sound emission, lets the player toggle
 * them on/off, adjust volume, rename, and select channels (for radios, record
 * players, etc.).
 *
 * The interface feels like a real device controller:
 *   - Scan animation with pulsing rings
 *   - Device cards with signal strength, toggle switches, volume sliders
 *   - Channel picker for multi-channel devices
 *   - Rename with inline text editing
 *   - Connection ripple animation on device selection
 *   - Distance-based RSSI signal bars
 *
 * This is the physical layer of audio control — the player's remote for
 * their living space.
 */

import type { GameState, FurniturePlacement } from "../../engine/state.js";
import { getFurnitureType } from "../../room/furniture.js";
import {
  type DeviceApp,
  type Rect,
  drawRoundRect,
  drawText,
  registerApp,
} from "../app.js";

// ─── Per-App State ──────────────────────────────────────────────────────

interface HToothDevice {
  furnitureId: string;
  name: string;        // customName or furniture type name
  typeName: string;    // original furniture type name
  isOn: boolean;
  volume: number;      // 0-1
  hasChannels: boolean;
  channels: string[];
  activeChannel: number;
  material: string;
  distance: number;    // tile distance from player
  signalBars: number;  // 0-4 RSSI signal bars
}

interface HToothState {
  scanning: boolean;
  scanTime: number;       // accumulator for scan animation
  devices: HToothDevice[];
  selectedIndex: number;  // -1 = no selection (list view)
  scrollY: number;
  scrollVelocity: number;
  renaming: boolean;
  renameBuffer: string;
  connectRipple: number;  // animation timer for connection ripple (0 = none)
  draggingVolume: boolean; // whether user is dragging the volume slider
  lastContentW: number;   // cached content width for drag calculations
}

function getState(state: GameState): HToothState {
  if (!state.player.device.appStates["htooth"]) {
    state.player.device.appStates["htooth"] = {
      scanning: false,
      scanTime: 0,
      devices: [],
      selectedIndex: -1,
      scrollY: 0,
      scrollVelocity: 0,
      renaming: false,
      renameBuffer: "",
      connectRipple: 0,
      draggingVolume: false,
      lastContentW: 200,
    };
  }
  return state.player.device.appStates["htooth"] as HToothState;
}

// ─── Helpers ────────────────────────────────────────────────────────────

function scanForDevices(state: GameState): HToothDevice[] {
  const room = state.currentRoom;
  if (!room) return [];

  const px = state.player.position.tileX;
  const py = state.player.position.tileY;

  const devices: HToothDevice[] = [];
  for (const f of room.furniture) {
    const ft = getFurnitureType(f.type);
    if (!ft || !ft.soundEmission) continue;

    const customName = f.state.customName as string | undefined;
    const volume = typeof f.state.audioVolume === "number" ? f.state.audioVolume : 0.75;
    const activeChannel = typeof f.state.audioChannel === "number" ? f.state.audioChannel : 0;
    const dx = f.tileX - px;
    const dy = f.tileY - py;
    const distance = Math.sqrt(dx * dx + dy * dy);
    // Signal strength: 4 bars at ≤2 tiles, 3 at ≤4, 2 at ≤7, 1 at ≤12, 0 beyond
    const signalBars = distance <= 2 ? 4 : distance <= 4 ? 3 : distance <= 7 ? 2 : distance <= 12 ? 1 : 0;

    devices.push({
      furnitureId: f.id,
      name: customName || ft.name,
      typeName: ft.name,
      isOn: Boolean(f.state.on || f.state.playing),
      volume,
      hasChannels: Boolean(ft.soundEmission.hasChannels && ft.soundEmission.channels?.length),
      channels: ft.soundEmission.channels ?? [],
      activeChannel,
      material: ft.material ?? "wood",
      distance,
      signalBars,
    });
  }
  // Sort by distance — closest devices first
  devices.sort((a, b) => a.distance - b.distance);
  return devices;
}

function getFurniture(state: GameState, id: string): FurniturePlacement | undefined {
  return state.currentRoom?.furniture.find(f => f.id === id);
}

// ─── Drawing Constants ─────────────────────────────────────────────────

const BG = "#0a0e14";
const CARD_BG = "#141c28";
const CARD_BORDER = "#1e2a3a";
const ACCENT = "#3a8aff";
const ACCENT_DIM = "#1a4a8a";
const ON_COLOR = "#22cc66";
const OFF_COLOR = "#444c56";
const TEXT_PRIMARY = "#e0e8f0";
const TEXT_SECONDARY = "#6a7a8a";
const TEXT_MUTED = "#3a4a5a";
const CARD_H = 72;
const CARD_PAD = 8;
const DETAIL_HEADER_H = 52;

// ─── Drawing Functions (standalone) ────────────────────────────────────

function drawSignalBars(ctx: CanvasRenderingContext2D, x: number, y: number, bars: number, isOn: boolean): void {
  const barW = 3;
  const gap = 2;
  const maxH = 14;
  for (let i = 0; i < 4; i++) {
    const h = 4 + i * 3;
    const bx = x + i * (barW + gap);
    const by = y + (maxH - h);
    ctx.fillStyle = i < bars
      ? (isOn ? ON_COLOR : ACCENT)
      : TEXT_MUTED;
    ctx.fillRect(bx, by, barW, h);
  }
}

function drawScanning(ctx: CanvasRenderingContext2D, rect: Rect, ht: HToothState): void {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h * 0.4;
  const t = ht.scanTime;

  // Pulsing rings
  for (let i = 0; i < 3; i++) {
    const phase = (t * 2 + i * 0.5) % 2;
    const r = 20 + phase * 40;
    const alpha = Math.max(0, 1 - phase / 2) * 0.4;
    ctx.strokeStyle = ACCENT;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Center dot with glow
  const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, 12);
  grd.addColorStop(0, ACCENT);
  grd.addColorStop(1, "transparent");
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.arc(cx, cy, 12, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = ACCENT;
  ctx.beginPath();
  ctx.arc(cx, cy, 6, 0, Math.PI * 2);
  ctx.fill();

  // Text + dots animation
  const dots = ".".repeat(Math.floor(t * 3) % 4);
  drawText(ctx, "Scanning" + dots, cx, cy + 60, rect.w - 32, "13px sans-serif", TEXT_SECONDARY, "center");
}

function drawList(ctx: CanvasRenderingContext2D, rect: Rect, ht: HToothState): void {
  const x = rect.x + CARD_PAD;
  const w = rect.w - CARD_PAD * 2;

  // Header
  drawText(ctx, "Nearby Devices", rect.x + 12, rect.y + 8, w, "bold 14px sans-serif", TEXT_PRIMARY);
  drawText(ctx, `${ht.devices.length} found`, rect.x + rect.w - 12, rect.y + 10, w, "11px sans-serif", TEXT_SECONDARY, "right");

  if (ht.devices.length === 0) {
    // Empty state — invite to scan
    drawText(ctx, "No audio devices in range.", rect.x + rect.w / 2, rect.y + rect.h * 0.35, w, "12px sans-serif", TEXT_MUTED, "center");
    drawText(ctx, "Place furniture with sound", rect.x + rect.w / 2, rect.y + rect.h * 0.35 + 18, w, "10px sans-serif", TEXT_MUTED, "center");
    drawText(ctx, "emission, then tap to scan.", rect.x + rect.w / 2, rect.y + rect.h * 0.35 + 32, w, "10px sans-serif", TEXT_MUTED, "center");
    // Scan button
    const btnW = 80; const btnH = 28;
    const btnX = rect.x + (rect.w - btnW) / 2;
    const btnY = rect.y + rect.h * 0.35 + 56;
    drawRoundRect(ctx, btnX, btnY, btnW, btnH, 14, ACCENT_DIM, ACCENT);
    drawText(ctx, "Scan", btnX + btnW / 2, btnY + 7, btnW, "bold 11px sans-serif", ACCENT, "center");
    return;
  }

  // Device cards
  const startY = rect.y + 32 - ht.scrollY;
  ctx.save();
  ctx.beginPath();
  ctx.rect(rect.x, rect.y + 28, rect.w, rect.h - 28);
  ctx.clip();

  for (let i = 0; i < ht.devices.length; i++) {
    const d = ht.devices[i];
    const cy = startY + i * (CARD_H + 6);
    if (cy + CARD_H < rect.y || cy > rect.y + rect.h) continue;

    // Card background
    drawRoundRect(ctx, x, cy, w, CARD_H, 8, CARD_BG, CARD_BORDER);

    // Status dot
    ctx.fillStyle = d.isOn ? ON_COLOR : OFF_COLOR;
    ctx.beginPath();
    ctx.arc(x + 16, cy + 20, 5, 0, Math.PI * 2);
    ctx.fill();
    // Glow for active devices
    if (d.isOn) {
      ctx.globalAlpha = 0.15;
      ctx.beginPath();
      ctx.arc(x + 16, cy + 20, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Device name + type
    drawText(ctx, d.name, x + 30, cy + 10, w - 70, "bold 12px sans-serif", TEXT_PRIMARY);
    drawText(ctx, d.typeName, x + 30, cy + 26, w - 70, "10px sans-serif", TEXT_SECONDARY);

    // Signal strength bars (top-right of card)
    drawSignalBars(ctx, x + w - 30, cy + 8, d.signalBars, d.isOn);

    // Volume bar (mini)
    const barX = x + 30;
    const barY = cy + 46;
    const barW = w - 80;
    const barH = 4;
    drawRoundRect(ctx, barX, barY, barW, barH, 2, TEXT_MUTED);
    if (d.volume > 0) {
      drawRoundRect(ctx, barX, barY, Math.max(4, barW * d.volume), barH, 2, d.isOn ? ACCENT : ACCENT_DIM);
    }

    // Volume text
    drawText(ctx, `${Math.round(d.volume * 100)}%`, x + w - 8, cy + 42, 40, "10px sans-serif", TEXT_SECONDARY, "right");

    // Distance text (bottom-right)
    const distText = d.distance < 1 ? "< 1m" : `${d.distance.toFixed(0)}m`;
    drawText(ctx, distText, x + w - 8, cy + 56, 40, "9px sans-serif", TEXT_MUTED, "right");

    // Chevron
    ctx.strokeStyle = TEXT_MUTED;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x + w - 14, cy + CARD_H / 2 - 6);
    ctx.lineTo(x + w - 8, cy + CARD_H / 2);
    ctx.lineTo(x + w - 14, cy + CARD_H / 2 + 6);
    ctx.stroke();
  }

  ctx.restore();
}

function drawDetail(ctx: CanvasRenderingContext2D, rect: Rect, state: GameState, ht: HToothState, dt: number): void {
  const d = ht.devices[ht.selectedIndex];
  if (!d) return;

  const x = rect.x + CARD_PAD;
  const w = rect.w - CARD_PAD * 2;
  let cy = rect.y + 10;

  // Connection ripple animation
  if (ht.connectRipple > 0) {
    ht.connectRipple -= dt;
    const t = 1 - Math.max(0, ht.connectRipple) / 0.6;
    const ripR = 20 + t * 60;
    const alpha = Math.max(0, 0.3 * (1 - t));
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(rect.x + rect.w / 2, cy + 16, ripR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // Back button
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x + 10, cy + 10);
  ctx.lineTo(x + 4, cy + 16);
  ctx.lineTo(x + 10, cy + 22);
  ctx.stroke();
  drawText(ctx, "Back", x + 18, cy + 8, 60, "12px sans-serif", ACCENT);

  // Signal + distance in header
  drawSignalBars(ctx, x + w - 30, cy + 6, d.signalBars, d.isOn);
  const distText = d.distance < 1 ? "< 1m" : `${d.distance.toFixed(0)}m`;
  drawText(ctx, distText, x + w - 36, cy + 10, 40, "9px sans-serif", TEXT_MUTED, "right");

  // Device name (tappable for rename)
  cy += DETAIL_HEADER_H;
  if (ht.renaming) {
    drawRoundRect(ctx, x, cy - 4, w, 28, 6, "#1a2230", ACCENT);
    drawText(ctx, ht.renameBuffer + "|", x + 8, cy + 2, w - 16, "bold 14px sans-serif", TEXT_PRIMARY);
  } else {
    drawText(ctx, d.name, x, cy, w, "bold 16px sans-serif", TEXT_PRIMARY);
    drawText(ctx, "tap to rename", x, cy + 20, w, "9px sans-serif", TEXT_MUTED);
  }
  cy += 44;

  // On/Off toggle
  const toggleW = 44;
  const toggleH = 24;
  const toggleX = x + w - toggleW - 4;
  const toggleY = cy;
  drawRoundRect(ctx, toggleX, toggleY, toggleW, toggleH, toggleH / 2, d.isOn ? ON_COLOR : OFF_COLOR);
  // Knob with shadow
  const knobR = 9;
  const knobX = d.isOn ? toggleX + toggleW - knobR - 3 : toggleX + knobR + 3;
  ctx.shadowColor = "rgba(0,0,0,0.3)";
  ctx.shadowBlur = 3;
  ctx.shadowOffsetX = 1;
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(knobX, toggleY + toggleH / 2, knobR, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  drawText(ctx, d.isOn ? "ON" : "OFF", x, cy + 4, 60, "bold 13px sans-serif", d.isOn ? ON_COLOR : TEXT_SECONDARY);
  cy += 40;

  // Volume slider
  drawText(ctx, "Volume", x, cy, 80, "11px sans-serif", TEXT_SECONDARY);
  drawText(ctx, `${Math.round(d.volume * 100)}%`, x + w, cy, 50, "10px sans-serif", TEXT_SECONDARY, "right");
  cy += 18;
  const sliderX = x;
  const sliderW = w;
  const sliderH = 6;
  drawRoundRect(ctx, sliderX, cy, sliderW, sliderH, 3, TEXT_MUTED);
  if (d.volume > 0) {
    drawRoundRect(ctx, sliderX, cy, Math.max(6, sliderW * d.volume), sliderH, 3, ACCENT);
  }
  // Slider thumb — outer ring + inner dot
  const thumbX = sliderX + sliderW * d.volume;
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(thumbX, cy + sliderH / 2, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = ACCENT;
  ctx.beginPath();
  ctx.arc(thumbX, cy + sliderH / 2, 5, 0, Math.PI * 2);
  ctx.fill();
  cy += 28;

  // Channel selector (if applicable)
  if (d.hasChannels && d.channels.length > 0) {
    drawText(ctx, "Channel", x, cy, 80, "11px sans-serif", TEXT_SECONDARY);
    cy += 18;
    for (let i = 0; i < d.channels.length; i++) {
      const isActive = i === d.activeChannel;
      const chanY = cy + i * 28;
      drawRoundRect(ctx, x, chanY, w, 24, 6,
        isActive ? ACCENT_DIM : CARD_BG,
        isActive ? ACCENT : CARD_BORDER);
      // Active indicator dot
      if (isActive) {
        ctx.fillStyle = ACCENT;
        ctx.beginPath();
        ctx.arc(x + 10, chanY + 12, 3, 0, Math.PI * 2);
        ctx.fill();
      }
      drawText(ctx, d.channels[i], x + (isActive ? 20 : 10), chanY + 5, w - 30, "11px sans-serif",
        isActive ? ACCENT : TEXT_PRIMARY);
    }
    cy += d.channels.length * 28 + 8;
  }

  // Material label — stylized
  const matLabel = d.material.charAt(0).toUpperCase() + d.material.slice(1);
  drawText(ctx, `Material: ${matLabel}`, x, cy, w, "10px sans-serif", TEXT_MUTED);
}

// ─── Volume slider helper ──────────────────────────────────────────────

function applyVolumeFromX(localX: number, ht: HToothState): boolean {
  const d = ht.devices[ht.selectedIndex];
  if (!d) return false;
  const sliderX = CARD_PAD;
  const sliderW = ht.lastContentW;
  const pct = Math.max(0, Math.min(1, (localX - sliderX) / sliderW));
  d.volume = pct;
  return true;
}

// ─── App Implementation ────────────────────────────────────────────────

const htoothApp: DeviceApp = {
  id: "htooth",
  name: "hTooth",
  iconGradient: ["#1a3a6a", "#0a1a3a"],

  drawIcon(ctx, cx, cy, size) {
    // Bluetooth-style icon: angular "B" made of lines
    const s = size * 0.3;
    ctx.strokeStyle = "#e0e8f0";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(cx - s, cy - s * 0.6);
    ctx.lineTo(cx + s * 0.4, cy + s * 0.6);
    ctx.lineTo(cx, cy);
    ctx.lineTo(cx + s * 0.4, cy - s * 0.6);
    ctx.lineTo(cx - s, cy + s * 0.6);
    ctx.stroke();
    // Vertical bar
    ctx.beginPath();
    ctx.moveTo(cx, cy - s);
    ctx.lineTo(cx, cy + s);
    ctx.stroke();
    // Small broadcast arcs
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.arc(cx, cy, s * 1.3, -Math.PI * 0.3, Math.PI * 0.3);
    ctx.stroke();
    ctx.globalAlpha = 0.3;
    ctx.beginPath();
    ctx.arc(cx, cy, s * 1.7, -Math.PI * 0.25, Math.PI * 0.25);
    ctx.stroke();
    ctx.globalAlpha = 1;
  },

  drawContent(ctx, rect, state, dt) {
    const ht = getState(state);
    ht.lastContentW = rect.w - CARD_PAD * 2;

    // Background
    ctx.fillStyle = BG;
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

    // Scan animation / momentum scroll
    if (ht.scanning) {
      ht.scanTime += dt;
      if (ht.scanTime > 1.5) {
        ht.scanning = false;
        ht.devices = scanForDevices(state);
      }
    }

    // Scroll momentum
    if (Math.abs(ht.scrollVelocity) > 0.5) {
      ht.scrollY += ht.scrollVelocity * dt;
      ht.scrollVelocity *= 0.92;
    } else {
      ht.scrollVelocity = 0;
    }
    ht.scrollY = Math.max(0, ht.scrollY);

    if (ht.selectedIndex >= 0 && ht.selectedIndex < ht.devices.length) {
      drawDetail(ctx, rect, state, ht, dt);
    } else if (ht.scanning) {
      drawScanning(ctx, rect, ht);
    } else {
      drawList(ctx, rect, ht);
    }
  },

  handleTap(localX, localY, state) {
    const ht = getState(state);

    // Detail view
    if (ht.selectedIndex >= 0) {
      const d = ht.devices[ht.selectedIndex];
      if (!d) return false;

      // Back button (top-left area)
      if (localY < DETAIL_HEADER_H) {
        ht.selectedIndex = -1;
        ht.renaming = false;
        return true;
      }

      // Name area (rename)
      if (localY >= DETAIL_HEADER_H && localY < DETAIL_HEADER_H + 40) {
        if (ht.renaming) {
          // Commit rename
          const f = getFurniture(state, d.furnitureId);
          if (f && ht.renameBuffer.trim()) {
            f.state.customName = ht.renameBuffer.trim();
            d.name = ht.renameBuffer.trim();
          }
          ht.renaming = false;
        } else {
          ht.renaming = true;
          ht.renameBuffer = d.name;
        }
        return true;
      }

      // Toggle area
      if (localY >= DETAIL_HEADER_H + 40 && localY < DETAIL_HEADER_H + 80) {
        const f = getFurniture(state, d.furnitureId);
        if (f) {
          if (f.state.playing !== undefined) {
            f.state.playing = !f.state.playing;
          } else {
            f.state.on = !f.state.on;
          }
          d.isOn = !d.isOn;
        }
        return true;
      }

      // Volume slider area (tap to set)
      if (localY >= DETAIL_HEADER_H + 80 && localY < DETAIL_HEADER_H + 126) {
        if (applyVolumeFromX(localX, ht)) {
          const f = getFurniture(state, d.furnitureId);
          if (f) f.state.audioVolume = d.volume;
        }
        return true;
      }

      // Channel selection
      if (d.hasChannels && d.channels.length > 0) {
        const channelStartY = DETAIL_HEADER_H + 126 + 18;
        const channelIdx = Math.floor((localY - channelStartY) / 28);
        if (channelIdx >= 0 && channelIdx < d.channels.length) {
          d.activeChannel = channelIdx;
          const f = getFurniture(state, d.furnitureId);
          if (f) f.state.audioChannel = channelIdx;
          return true;
        }
      }

      return false;
    }

    // Scanning state — do nothing
    if (ht.scanning) return false;

    // List view
    if (ht.devices.length === 0) {
      // Tap anywhere to scan
      ht.scanning = true;
      ht.scanTime = 0;
      return true;
    }

    // Card tap — select device + connection ripple
    const startY = 32 - ht.scrollY;
    for (let i = 0; i < ht.devices.length; i++) {
      const cardY = startY + i * (CARD_H + 6);
      if (localY >= cardY && localY < cardY + CARD_H) {
        ht.selectedIndex = i;
        ht.renaming = false;
        ht.connectRipple = 0.6; // trigger connection animation
        return true;
      }
    }

    return false;
  },

  handleDrag(dy, state) {
    const ht = getState(state);
    if (ht.selectedIndex >= 0 && ht.draggingVolume) {
      // Volume drag — horizontal only. dy is actually dx in some device
      // frameworks, but we handle it in handlePointerMove if available.
      // For now, list scrolling only happens when NOT in detail view.
      return;
    }
    if (ht.selectedIndex < 0) {
      ht.scrollY -= dy;
      ht.scrollY = Math.max(0, ht.scrollY);
      ht.scrollVelocity = -dy * 60;
    }
  },

  handleDragEnd(velocityY, state) {
    const ht = getState(state);
    ht.scrollVelocity = velocityY;
    ht.draggingVolume = false;
  },

  handlePointerMove(localX, localY, state) {
    const ht = getState(state);
    // Drag-to-adjust volume in detail view
    if (ht.selectedIndex >= 0 && localY >= DETAIL_HEADER_H + 80 && localY < DETAIL_HEADER_H + 126) {
      ht.draggingVolume = true;
      const d = ht.devices[ht.selectedIndex];
      if (d && applyVolumeFromX(localX, ht)) {
        const f = getFurniture(state, d.furnitureId);
        if (f) f.state.audioVolume = d.volume;
      }
    } else {
      ht.draggingVolume = false;
    }
  },

  onOpen(state) {
    const ht = getState(state);
    // Auto-scan on open
    ht.scanning = true;
    ht.scanTime = 0;
    ht.selectedIndex = -1;
    ht.renaming = false;
    ht.connectRipple = 0;
    ht.draggingVolume = false;
  },

  onBack(state) {
    const ht = getState(state);
    if (ht.renaming) {
      ht.renaming = false;
      return true;
    }
    if (ht.selectedIndex >= 0) {
      ht.selectedIndex = -1;
      return true;
    }
    return false;
  },

  badge(state) {
    // Show badge if any device is ON
    const room = state.currentRoom;
    if (!room) return 0;
    let count = 0;
    for (const f of room.furniture) {
      const ft = getFurnitureType(f.type);
      if (ft?.soundEmission && (f.state.on || f.state.playing)) count++;
    }
    return count;
  },
};

registerApp(htoothApp);
